import { ChangeDetectorRef, Component } from '@angular/core';
import { DataType, DataTypeComponent } from '../../data-type-component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PersonalLifetimeThresholdsService } from './personal-lifetime-thresholds.service';
import { Action, LifetimeThresholdType, PersonalLifetimeThresholdDTO } from './personal-lifetime-thresholds.model';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-personal-lifetime-thresholds',
  templateUrl: './personal-lifetime-thresholds.html',
  styleUrls: ['./personal-lifetime-thresholds.component.scss'],
  standalone: false,
})
export class PersonalLifetimeThresholdsComponent extends DataTypeComponent {
  fg: UntypedFormGroup;
  currencyCode: string;
  playerId: number;
  success: boolean = false;
  updatingInProgress: boolean = false;
  lifetimeThresholdType: LifetimeThresholdType;
  lifetimeThresholdText: string;

  constructor(
    private fb: UntypedFormBuilder,
    private service: PersonalLifetimeThresholdsService,
    private changesDetector: ChangeDetectorRef
  ) {
    super();
  }

  override setRowData(entity: any) {
    this.currencyCode = entity?.currencyCode;
    this.playerId = entity.details?.playerId;
    this.initializeThresholdType();
    this.initialize();
  }

  update() {
    this.updatingInProgress = true;
    const rawValue = this.fg.getRawValue();
    const payload = {
      active: rawValue.active,
      value: rawValue.value,
      actions: []
        .concat(rawValue.action ? [Action.mapToAction(rawValue.action)] : [])
        .concat(rawValue.jiraTicket ? [Action.CREATE_JIRA_TICKET] : []),
    };

    switch (this.lifetimeThresholdType) {
      case LifetimeThresholdType.DEPOSIT:
        this.updateValuesAfterUpdateApiCall(this.service.updateDeposit(this.playerId, payload));
        break;
      case LifetimeThresholdType.NET_LOSS:
        this.updateValuesAfterUpdateApiCall(this.service.updateNetLoss(this.playerId, payload));
        break;
    }
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.fg.enable();
    } else {
      this.initialize();
    }
  }

  private initialize() {
    switch (this.lifetimeThresholdType) {
      case LifetimeThresholdType.DEPOSIT:
        this.service.getDeposit(this.playerId).subscribe((d) => this.setupForm(d));
        break;
      case LifetimeThresholdType.NET_LOSS:
        this.service.getNetLoss(this.playerId).subscribe((d) => this.setupForm(d));
        break;
    }
  }

  private setupForm(d: PersonalLifetimeThresholdDTO): void {
    this.fg = this.fb.group({
      active: [d.active, Validators.required],
      value: [this.currencyCode + ' ' + (d.value?.split(' ')[1] || '0.00')],
      action: [this.getMandatoryAction(d.actions)],
      playerMonitoring: [d.actions?.includes(Action.CREATE_ZENDESK_TICKET)],
      freeze: [this.getMandatoryAction(d.actions) === 'freeze'],
      depositBlock: [this.getMandatoryAction(d.actions) === 'depositBlock'],
      jiraTicket: [d.actions?.includes(Action.CREATE_JIRA_TICKET)],
    });
    this.changesDetector.detectChanges();
    this.fg.disable();
  }

  private updateValuesAfterUpdateApiCall(observable: Observable<void>) {
    observable.subscribe(
      () => {
        this.toggleEditMode();
        this.fg.disable();
        this.success = true;
        setTimeout(() => {
          this.success = false;
        }, 2000);
        this.updatingInProgress = false;
      },
      () => {
        this.success = false;
        this.updatingInProgress = false;
      }
    );
  }

  private initializeThresholdType() {
    switch (this.fieldConfig?.type) {
      case DataType.personal_lifetime_deposits_thresholds:
        this.lifetimeThresholdType = LifetimeThresholdType.DEPOSIT;
        this.lifetimeThresholdText = 'DEPOSITS';
        break;
      case DataType.personal_lifetime_net_loss_thresholds:
        this.lifetimeThresholdType = LifetimeThresholdType.NET_LOSS;
        this.lifetimeThresholdText = 'NET LOSS';
        break;
    }
  }

  private getMandatoryAction(actions: Action[]): string | undefined {
    if (actions?.includes(Action.FREEZE_ACCOUNT)) {
      return 'freeze';
    }
    if (actions?.includes(Action.BLOCK_DEPOSIT)) {
      return 'depositBlock';
    }
    if (actions?.includes(Action.ACCOUNT_TIMER)) {
      return 'accountTimer';
    }
    return undefined;
  }
}
